/**
 * StatusDashboard — main status page dashboard component.
 *
 * Features:
 *   - Overall status banner
 *   - Component grid with status pills
 *   - Active incidents section (expandable cards with timelines)
 *   - Scheduled maintenance section
 *   - SSE connection for real-time updates with pulse animation on status changes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { statusApi } from '../../api/status.js';
import type {
  StatusComponent,
  IncidentWithDetails,
  MaintenanceWindow,
  ComponentStatus as ComponentStatusType,
  SseEventType,
} from '../../api/status.js';
import { ComponentStatus, StatusPill } from './ComponentStatus.js';
import { IncidentTimeline } from './IncidentTimeline.js';
import { MaintenanceSchedule } from './MaintenanceSchedule.js';

// ── Overall status banner ─────────────────────────────────────────────────────

function computeOverallStatus(components: StatusComponent[]): ComponentStatusType {
  if (components.length === 0) return 'operational';
  if (components.some((c) => c.status === 'major_outage')) return 'major_outage';
  if (components.some((c) => c.status === 'partial_outage')) return 'partial_outage';
  if (components.some((c) => c.status === 'degraded')) return 'degraded';
  return 'operational';
}

const OVERALL_LABELS: Record<ComponentStatusType, string> = {
  operational:    'All Systems Operational',
  degraded:       'Some Systems Degraded',
  partial_outage: 'Partial System Outage',
  major_outage:   'Major System Outage',
  maintenance:    'Maintenance In Progress',
};

const OVERALL_COLORS: Record<ComponentStatusType, { bg: string; border: string; text: string; dot: string }> = {
  operational:    { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  degraded:       { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-800',   dot: 'bg-amber-400' },
  partial_outage: { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-800',  dot: 'bg-orange-500' },
  major_outage:   { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     dot: 'bg-red-500' },
  maintenance:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800',    dot: 'bg-blue-400' },
};

// ── Incident card ─────────────────────────────────────────────────────────────

const SEVERITY_COLORS = {
  minor:    { bg: 'bg-neutral-100', text: 'text-neutral-700' },
  major:    { bg: 'bg-amber-100',   text: 'text-amber-800' },
  critical: { bg: 'bg-red-100',     text: 'text-red-800' },
};

const INCIDENT_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  detected:      { bg: 'bg-neutral-100', text: 'text-neutral-700' },
  investigating: { bg: 'bg-red-100',     text: 'text-red-800' },
  identified:    { bg: 'bg-orange-100',  text: 'text-orange-800' },
  monitoring:    { bg: 'bg-blue-100',    text: 'text-blue-800' },
  resolved:      { bg: 'bg-emerald-100', text: 'text-emerald-800' },
};

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface IncidentCardProps {
  incident: IncidentWithDetails;
  highlighted: boolean;
}

function IncidentCard({ incident, highlighted }: IncidentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const sevColors = SEVERITY_COLORS[incident.severity] ?? SEVERITY_COLORS.major;
  const statusColors = INCIDENT_STATUS_COLORS[incident.status] ?? INCIDENT_STATUS_COLORS.investigating;

  return (
    <div
      className={`border rounded-lg transition-all duration-300 ${
        highlighted
          ? 'border-blue-400 shadow-md ring-2 ring-blue-200'
          : 'border-neutral-200 hover:shadow-sm'
      }`}
    >
      <button
        className="w-full text-left px-4 py-3 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-blue-500 rounded-lg"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-semibold text-neutral-900 text-sm">{incident.title}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sevColors.text} ${sevColors.bg}`}>
                {incident.severity}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors.text} ${statusColors.bg}`}>
                {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
              </span>
            </div>

            <p className="text-xs text-neutral-500">
              Started {relativeTime(incident.created_at)}
              {incident.resolved_at && ` · Resolved ${relativeTime(incident.resolved_at)}`}
              {incident.source === 'monitoring_alert' && (
                <span className="ml-2 text-neutral-400">· Auto-detected</span>
              )}
            </p>

            {incident.affected_component_names.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {incident.affected_component_names.map((name, i) => (
                  <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-neutral-100 text-neutral-600">
                    {name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <span className="text-neutral-400 text-xs flex-shrink-0 mt-0.5">
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-neutral-100 pt-3">
          <IncidentTimeline updates={incident.updates} incidentId={incident.id} />
        </div>
      )}
    </div>
  );
}

// ── StatusDashboard ───────────────────────────────────────────────────────────

export function StatusDashboard() {
  const [components, setComponents] = useState<StatusComponent[]>([]);
  const [incidents, setIncidents] = useState<IncidentWithDetails[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pulsedComponentIds, setPulsedComponentIds] = useState<Set<string>>(new Set());
  const [highlightedIncidentIds, setHighlightedIncidentIds] = useState<Set<string>>(new Set());

  const pulseTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Initial data fetch ────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    try {
      const [compRes, incRes, maintRes] = await Promise.all([
        statusApi.getComponents(),
        statusApi.getIncidents({ active: true }),
        statusApi.getMaintenanceWindows().catch(() => ({ maintenance_windows: [] })),
      ]);
      setComponents(compRes.components);
      setIncidents(incRes.incidents);
      setMaintenance(maintRes.maintenance_windows);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // ── SSE connection ────────────────────────────────────────────────────────

  useEffect(() => {
    const cleanup = statusApi.connectToStream(
      (type: SseEventType, data: Record<string, unknown>, _eventId: number) => {
        setLastUpdated(new Date());

        if (type === 'component_status') {
          const { component_id, new_status } = data as {
            component_id: string;
            new_status: string;
          };

          // Update component status in state
          setComponents((prev) =>
            prev.map((c) =>
              c.id === component_id
                ? { ...c, status: new_status as ComponentStatusType }
                : c,
            ),
          );

          // Trigger pulse animation
          setPulsedComponentIds((prev) => new Set([...prev, component_id]));
          if (pulseTimeout.current) clearTimeout(pulseTimeout.current);
          pulseTimeout.current = setTimeout(() => {
            setPulsedComponentIds(new Set());
          }, 2000);
        }

        if (type === 'incident_created') {
          const { incident, initial_update } = data as {
            incident: IncidentWithDetails;
            initial_update: import('../../api/status.js').IncidentUpdate;
          };

          setIncidents((prev) => [
            {
              ...incident,
              updates: [initial_update],
              affected_component_names: incident.affected_component_names ?? [],
            },
            ...prev,
          ]);

          // Highlight the new incident
          setHighlightedIncidentIds((prev) => new Set([...prev, incident.id]));
          if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
          highlightTimeout.current = setTimeout(() => {
            setHighlightedIncidentIds(new Set());
          }, 5000);
        }

        if (type === 'incident_updated') {
          const { incident_id, update, incident } = data as {
            incident_id: string;
            update: import('../../api/status.js').IncidentUpdate;
            incident: IncidentWithDetails;
          };

          setIncidents((prev) =>
            prev.map((inc) => {
              if (inc.id !== incident_id) return inc;
              return {
                ...inc,
                ...incident,
                updates: [...(inc.updates ?? []), update],
              };
            }),
          );
        }

        if (type === 'maintenance_started' || type === 'maintenance_completed') {
          // Refresh maintenance windows
          statusApi.getMaintenanceWindows()
            .then((res) => setMaintenance(res.maintenance_windows))
            .catch(() => {});
        }
      },
      () => {
        // SSE error — fall back to polling
        setTimeout(() => void fetchData(), 30_000);
      },
    );

    return cleanup;
  }, [fetchData]);

  // ── Cleanup ───────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (pulseTimeout.current) clearTimeout(pulseTimeout.current);
      if (highlightTimeout.current) clearTimeout(highlightTimeout.current);
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  const overallStatus = computeOverallStatus(components);
  const overallColors = OVERALL_COLORS[overallStatus];

  const activeIncidents = incidents.filter((i) => i.status !== 'resolved');
  const resolvedIncidents = incidents.filter((i) => i.status === 'resolved').slice(0, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-16 bg-neutral-200 rounded-xl" />
            <div className="h-64 bg-neutral-200 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error && components.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center max-w-md">
          <p className="text-neutral-700 mb-4">{error}</p>
          <button
            onClick={() => void fetchData()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-neutral-900">System Status</h1>
          {lastUpdated && (
            <p className="text-sm text-neutral-500 mt-1">
              Real-time updates · Last refreshed {relativeTime(lastUpdated.toISOString())}
            </p>
          )}
        </div>

        {/* Overall status banner */}
        <div
          className={`rounded-xl border px-5 py-4 flex items-center gap-3 ${overallColors.bg} ${overallColors.border}`}
        >
          <span
            className={`w-3 h-3 rounded-full flex-shrink-0 ${overallColors.dot} ${
              overallStatus !== 'operational' ? 'animate-pulse' : ''
            }`}
            aria-hidden="true"
          />
          <span className={`font-semibold ${overallColors.text}`}>
            {OVERALL_LABELS[overallStatus]}
          </span>
        </div>

        {/* Component grid */}
        <section aria-labelledby="components-heading">
          <h2 id="components-heading" className="text-lg font-semibold text-neutral-800 mb-3">
            Components
          </h2>
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm divide-y divide-neutral-100">
            {components.map((component) => (
              <div
                key={component.id}
                className={`px-4 py-3 transition-all duration-500 ${
                  pulsedComponentIds.has(component.id)
                    ? 'bg-blue-50 ring-1 ring-inset ring-blue-200'
                    : ''
                }`}
              >
                <ComponentStatus component={component} showUptimeGrid={false} />
              </div>
            ))}
            {components.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-neutral-500">
                No components available
              </div>
            )}
          </div>
        </section>

        {/* Maintenance windows */}
        <MaintenanceSchedule windows={maintenance} />

        {/* Active incidents */}
        {activeIncidents.length > 0 && (
          <section aria-labelledby="active-incidents-heading">
            <h2 id="active-incidents-heading" className="text-lg font-semibold text-neutral-800 mb-3">
              Active Incidents
            </h2>
            <div className="space-y-3">
              {activeIncidents.map((incident) => (
                <IncidentCard
                  key={incident.id}
                  incident={incident}
                  highlighted={highlightedIncidentIds.has(incident.id)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Recent resolved incidents */}
        {resolvedIncidents.length > 0 && (
          <section aria-labelledby="resolved-incidents-heading">
            <h2 id="resolved-incidents-heading" className="text-lg font-semibold text-neutral-800 mb-3">
              Recent Incidents
            </h2>
            <div className="space-y-3">
              {resolvedIncidents.map((incident) => (
                <IncidentCard key={incident.id} incident={incident} highlighted={false} />
              ))}
            </div>
          </section>
        )}

        {activeIncidents.length === 0 && resolvedIncidents.length === 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm px-5 py-8 text-center">
            <p className="text-neutral-500 text-sm">No incidents in the last 30 days</p>
          </div>
        )}
      </div>
    </div>
  );
}
